// Videos' "Fix or change a video" (spec ui-perf/simple-video-tools): the prototype's eight tools,
// in its order and words, and the page of each: the Studio's own form for the tool, or Pro's video
// form opened on its action (spec ui-perf/simple-video-compose-tools).
import {
  ArrowRightToLine,
  Eraser,
  Footprints,
  Languages,
  type LucideIcon,
  MessageSquareText,
  Proportions,
  Scaling,
  Wand,
} from "lucide-react";
import type { ReactElement } from "react";

import type { GenerationType } from "../../../api/generated/model";
import type { ToolFormType } from "../../../platform/studio/tools";
import { BackgroundForm } from "../../../platform/studio/tools/BackgroundForm";
import { DubbingForm } from "../../../platform/studio/tools/DubbingForm";
import type { ToolFormProps } from "../../../platform/studio/tools/form-parts";
import { LipSyncForm } from "../../../platform/studio/tools/LipSyncForm";
import { MotionForm } from "../../../platform/studio/tools/MotionForm";
import { ReframeForm, UpscaleForm } from "../../../platform/studio/tools/ToolForms";

/** A tool's page, `simple/videos/<slug>`, and its results. */
export const TOOL_SLUGS = ["upscale", "reframe", "dub", "lipsync", "extend", "edit", "background", "motion"] as const;
export type ToolSlug = (typeof TOOL_SLUGS)[number];

/** A tool with the Studio's form of its own, for its job type. */
interface FormPage {
  slug: ToolSlug;
  type: ToolFormType;
  Form: (props: ToolFormProps) => ReactElement | null;
  /** Its results' heading: "Recent <noun> results". */
  noun: string;
}

/**
 * A tool that runs on the video composer: Pro's video form, opened on its action. Its tabs stay
 * the person's, so its results are every video, as Pro's Video page's are.
 */
interface ComposerPage {
  slug: ToolSlug;
  type: Extract<GenerationType, "video.extend" | "video.edit">;
  action: "videoExtend" | "videoEdit";
}

export type ToolPage = FormPage | ComposerPage;

export interface VideoTool {
  name: string;
  line: string;
  icon: LucideIcon;
  /** None for a tool with no page in Simple yet: Coming soon. */
  page?: ToolPage | undefined;
}

export const VIDEO_TOOLS: readonly VideoTool[] = [
  {
    name: "Make it sharper",
    line: "Upscale a video up to 4x.",
    icon: Scaling,
    page: { slug: "upscale", type: "video.upscale", Form: UpscaleForm, noun: "upscale" },
  },
  {
    name: "Change shape",
    line: "New aspect ratio. The new edges are generated, nothing is cropped.",
    icon: Proportions,
    page: { slug: "reframe", type: "video.reframe", Form: ReframeForm, noun: "reframe" },
  },
  {
    name: "Translate the voice",
    line: "Same video, re-voiced in another language.",
    icon: Languages,
    page: { slug: "dub", type: "video.dub", Form: DubbingForm, noun: "dub" },
  },
  {
    name: "Change what they say",
    line: "New words, lips matched.",
    icon: MessageSquareText,
    page: { slug: "lipsync", type: "video.lipsync", Form: LipSyncForm, noun: "lip-sync" },
  },
  {
    name: "Make it longer",
    line: "Continue a video past its last frame.",
    icon: ArrowRightToLine,
    page: { slug: "extend", type: "video.extend", action: "videoExtend" },
  },
  {
    name: "Change something",
    line: "Change a person, a background or an outfit in a video.",
    icon: Wand,
    page: { slug: "edit", type: "video.edit", action: "videoEdit" },
  },
  {
    name: "Remove background",
    line: "Cut out the person or product.",
    icon: Eraser,
    page: { slug: "background", type: "video.background", Form: BackgroundForm, noun: "background removal" },
  },
  {
    name: "Copy a move",
    line: "Your actor copies the moves from another video.",
    icon: Footprints,
    page: { slug: "motion", type: "video.motion", Form: MotionForm, noun: "motion control" },
  },
];

/** The tool a page's slug names. */
export function toolOf(slug: ToolSlug): VideoTool & { page: ToolPage } {
  const found = VIDEO_TOOLS.find((each) => each.page?.slug === slug);
  if (found?.page === undefined) throw new Error(`no tool page "${slug}"`);
  return { ...found, page: found.page };
}

export const isToolSlug = (slug: string): slug is ToolSlug => (TOOL_SLUGS as readonly string[]).includes(slug);
